"use client";

import { useState, useEffect } from "react";
import {
  MagnifyingGlass,
  X,
  ArrowClockwise,
  WarningCircle,
  SpinnerGap,
  CalendarBlank,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  PlusCircle,
  Eye,
  Globe,
  User,
  Buildings,
  PencilSimple,
  CaretDown,
  ArrowDownRight,
  ArrowUpRight,
  FunnelSimple,
} from "phosphor-react";

import { DateRangePicker, SearchableSelect } from "../../components";
import providersService from "../../services/providersService";
import usersService from "../../services/usersService";
import clientsService from "../../services/clientsService";
import transferHistoryService from "../../services/transferHistoryService";
import TransactionDetailsModal from "../AdAccountDetails/TransactionDetailsModal";
import ChangeStatusModal from "../AdAccountDetails/ChangeStatusModal";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { useCurrentUser } from "../../utils/context/UserProvider";
import toast from "react-hot-toast"

/* ────────────────────────────── Platform Icons ────────────────────────────── */
const getPlatformIcon = (platform) => {
  switch (platform) {
    case "meta":
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#0081fb"
            d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5 c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28 c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51 c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40 c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35 c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57 c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5 c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
          ></path>
          <linearGradient id="meta_g1" x1="42.304" x2="13.533" y1="24.75" y2="24.75" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#0081fb"></stop>
            <stop offset=".995" stopColor="#0064e1"></stop>
          </linearGradient>
          <path
            fill="url(#meta_g1)"
            d="M4.918,15.456 C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967 c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362 c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333 c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754 c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
          ></path>
        </svg>
      );
    case "google":
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
          <polygon fill="#ffc107" points="30.129,15.75 18.871,9.25 5.871,31.25 17.129,37.75"></polygon>
          <path
            fill="#1e88e5"
            d="M31.871,37.75c1.795,3.109,5.847,4.144,8.879,2.379c3.103-1.806,4.174-5.77,2.379-8.879l-13-22 c-1.795-3.109-5.835-4.144-8.879-2.379c-3.106,1.801-4.174,5.77-2.379,8.879L31.871,37.75z"
          ></path>
          <circle cx="11.5" cy="34.5" r="6.5" fill="#43a047"></circle>
        </svg>
      );
    case "tiktok":
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#212121"
            fillRule="evenodd"
            d="M10.904,6h26.191C39.804,6,42,8.196,42,10.904v26.191 C42,39.804,39.804,42,37.096,42H10.904C8.196,42,6,39.804,6,37.096V10.904C6,8.196,8.196,6,10.904,6z"
            clipRule="evenodd"
          ></path>
          <path
            fill="#ec407a"
            fillRule="evenodd"
            d="M29.208,20.607c1.576,1.126,3.507,1.788,5.592,1.788v-4.011 c-0.395,0-0.788-0.041-1.174-0.123v3.157c-2.085,0-4.015-0.663-5.592-1.788v8.184c0,4.094-3.321,7.413-7.417,7.413 c-1.528,0-2.949-0.462-4.129-1.254c1.347,1.376,3.225,2.23,5.303,2.23c4.096,0,7.417-3.319,7.417-7.413L29.208,20.607z M30.657,16.561c-0.805-0.879-1.334-2.016-1.449-3.273v-0.516h-1.113C28.375,14.369,29.331,15.734,30.657,16.561z M19.079,30.832c-0.45-0.59-0.693-1.311-0.692-2.053c0-1.873,1.519-3.391,3.393-3.391c0.349,0,0.696,0.053,1.029,0.159v-4.1 c-0.389-0.053-0.781-0.076-1.174-0.068v3.191c-0.333-0.106-0.68-0.159-1.03-0.159c-1.874,0-3.393,1.518-3.393,3.391 C17.213,29.127,17.972,30.274,19.079,30.832z"
            clipRule="evenodd"
          ></path>
          <path
            fill="#fff"
            fillRule="evenodd"
            d="M28.034,19.63c1.576,1.126,3.507,1.788,5.592,1.788v-3.157 c-1.164-0.248-2.194-0.856-2.969-1.701c-1.326-0.827-2.281-2.191-2.561-3.788h-2.923v16.018c-0.007,1.867-1.523,3.379-3.393,3.379 c-1.102,0-2.081-0.525-2.701-1.338c-1.107-0.558-1.866-1.705-1.866-3.029c0-1.873,1.519-3.391,3.393-3.391 c0.359,0,0.705,0.056,1.03,0.159V21.38c-4.024,0.083-7.26,3.369-7.26,7.411c0,2.018,0.806,3.847,2.114,5.183 c1.18,0.792,2.601,1.254,4.129,1.254c4.096,0,7.417-3.319,7.417-7.413z"
            clipRule="evenodd"
          ></path>
          <path
            fill="#81d4fa"
            fillRule="evenodd"
            d="M33.626,18.262v-0.854c-1.05,0.002-2.078-0.292-2.969-0.848 C31.445,17.423,32.483,18.018,33.626,18.262z M28.095,12.772c-0.027-0.153-0.047-0.306-0.061-0.461v-0.516h-4.036v16.019 c-0.006,1.867-1.523,3.379-3.393,3.379c-0.549,0-1.067-0.13-1.526-0.362c0.62,0.813,1.599,1.338,2.701,1.338 c1.87,0,3.386-1.512,3.393-3.379V12.772H28.095z M21.635,21.38v-0.909c-0.337-0.046-0.677-0.069-1.018-0.069 c-4.097,0-7.417,3.319-7.417,7.413c0,2.567,1.305,4.829,3.288,6.159c-1.308-1.336-2.114-3.165-2.114-5.183 C14.374,24.749,17.611,21.463,21.635,21.38z"
            clipRule="evenodd"
          ></path>
        </svg>
      );
    case "snapchat":
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#ffeb3b"
            d="M24,4C12.402,4,3,13.402,3,25c0,11.598,9.402,21,21,21s21-9.402,21-21C45,13.402,35.598,4,24,4z"
          ></path>
          <path
            fill="#fff"
            d="M24,36c-2.451,0-4.045-1.09-4.827-1.612c-0.477-0.317-0.924-0.616-1.317-0.705 c-0.345-0.078-0.685-0.118-1.012-0.118c-0.59,0-1.056,0.103-1.386,0.175c-0.207,0.045-0.385,0.084-0.526,0.084 c-0.143,0-0.298-0.032-0.367-0.267c-0.064-0.218-0.107-0.428-0.148-0.631c-0.116-0.568-0.198-0.92-0.416-0.954 c-2.562-0.401-3.295-0.945-3.464-1.331c-0.024-0.055-0.037-0.11-0.04-0.165c-0.008-0.138,0.09-0.258,0.227-0.283 c3.731-0.614,5.399-4.44,5.467-4.589c0.004-0.009,0.008-0.017,0.012-0.026c0.225-0.456,0.268-0.851,0.131-1.174 c-0.258-0.611-1.099-0.879-1.657-1.058c-0.137-0.044-0.268-0.086-0.371-0.126c-0.504-0.198-1.349-0.612-1.241-1.186 c0.078-0.416,0.615-0.707,1.049-0.707c0.12,0,0.226,0.021,0.316,0.063c0.518,0.242,0.986,0.364,1.392,0.364 c0.558,0,0.802-0.236,0.833-0.266c-0.015-0.273-0.033-0.557-0.051-0.846c-0.119-1.922-0.268-4.313,0.337-5.674 C18.915,13.058,22.304,13,23.104,13h0.792c0.8,0,4.189,0.058,6.254,3.347c0.605,1.359,0.456,3.75,0.337,5.673 c-0.018,0.29-0.036,0.573-0.051,0.847c0.031,0.03,0.275,0.266,0.833,0.266c0.406,0,0.874-0.122,1.392-0.364 c0.09-0.042,0.196-0.063,0.316-0.063c0.433,0,0.971,0.291,1.049,0.707c0.108,0.574-0.737,0.988-1.241,1.186 c-0.103,0.04-0.234,0.082-0.371,0.126c-0.558,0.179-1.399,0.447-1.657,1.058c-0.137,0.323-0.094,0.718,0.131,1.174 c0.004,0.009,0.008,0.017,0.012,0.026c0.068,0.149,1.736,3.975,5.467,4.589c0.137,0.025,0.235,0.145,0.227,0.283 c-0.003,0.055-0.016,0.11-0.04,0.165c-0.169,0.386-0.902,0.93-3.464,1.331c-0.218,0.034-0.224,0.267-0.367,0.267 c-0.142,0-0.319-0.039-0.526-0.084 c-0.33-0.072-0.796-0.175-1.386-0.175c-0.327,0-0.667,0.04-1.012,0.118c-0.393,0.089-0.84,0.388-1.317,0.705 C28.045,34.91,26.451,36,24,36z"
          ></path>
        </svg>
      );
    default:
      return <Globe size={18} className="text-neutral-800 dark:text-neutral-400" />;
  }
};

const DEFAULT_FILTERS = {
  type: "",
  status: "",
  platform: "",
  admin: "",
  client: "",
  provider: "",
};

const DEFAULT_RANGE = { start: "", end: "" };

/* ────────────────────────────── Component ────────────────────────────── */
export default function FinancialReport() {
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const { currentUser } = useCurrentUser();
  const { checkAccess } = useAccessChecker();
  const canRead = checkAccess("topups", "read");
  const canUpdate = checkAccess("topups", "update");
  // One flag per filter, each matching the permission that filter's OWN
  // request needs. Grouping them caused a role with clients.read but no
  // users.read to fire the users request and get a 403 toast on page load.
  // NOTE: must stay below the currentUser declaration these read.
  const isStaff = currentUser?.role?.name !== "Client";
  const canSeeAdminFilter = isStaff && checkAccess("users", "read");
  const canSeeClientFilter = isStaff && checkAccess("clients", "read");
  const canSeeProviderFilter =
    isStaff &&
    (checkAccess("providers", "read") ||
      checkAccess("statistics", "read") ||
      checkAccess("adAccounts", "create") ||
      checkAccess("adAccounts", "update"));
  const canSeeStaffFilters =
    canSeeAdminFilter || canSeeClientFilter || canSeeProviderFilter;

  const [adminOptions, setAdminOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [dateRange, setDateRange] = useState(DEFAULT_RANGE);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [totals, setTotals] = useState({ totalTopUp: 0, totalWithdraw: 0 });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalPages: 1,
    totalItems: 0,
  });

  const [filtersActive, setFiltersActive] = useState(false);

  /* Title */
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: { title: "Top-Ups & Transfers History" },
    });
    window.dispatchEvent(event);
    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      });
      window.dispatchEvent(resetEvent);
    };
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".transaction-dropdown")) setDropdownOpen(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const statusOptions = [
    { id: "all-statuses", name: "All Statuses", value: "" },
    { id: "completed", name: "Completed", value: "completed" },
    { id: "pending", name: "Processing", value: "pending" },
    { id: "rejected", name: "Rejected", value: "rejected" },
    { id: "reversed", name: "Reversed", value: "reversed" },
  ];

  const typeOptions = [
    { id: "all-types", name: "All Types", value: "" },
    { id: "Transfer", name: "Transfer", value: "Transfer" },
    { id: "Reversal", name: "Reversal", value: "Reversal" },
    { id: "Top-Down", name: "Withdraw", value: "Top-Down" },
    { id: "Top-Up", name: "Top-Up", value: "Top-Up" },
  ];

  const platformOptions = [
    { id: "all-platforms", name: "All Platforms", value: "" },
    { id: "meta", name: "Meta", value: "meta" },
    { id: "google", name: "Google", value: "google" },
    { id: "tiktok", name: "TikTok", value: "tiktok" },
    { id: "snapchat", name: "Snapchat", value: "snapchat" },
  ];

  /* Filters active? */
  useEffect(() => {
    const hasActiveFilters =
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.type !== "" ||
      filters.status !== "" ||
      filters.platform !== "" ||
      filters.admin !== "" ||
      filters.provider !== "" ||
      filters.client !== "";

    setFiltersActive(hasActiveFilters);
  }, [searchQuery, dateRange, filters]);

  const getClientStatusDisplay = (status) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "pending":
        return "Processing";
      case "rejected":
        return "Rejected";
      case "reversed":
        return "Reversed";
      default:
        return status?.[0]?.toUpperCase() + status?.slice(1);
    }
  };

  const formatCurrency = (amount, currency = "USD") =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(amount || 0);

  const getPlatformDisplayName = (p) => (p ? p[0].toUpperCase() + p.slice(1) : "");

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60";
      case "pending":
        return "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-100 dark:border-yellow-800/60";
      case "rejected":
        return "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-100 dark:border-red-800/60";
      case "reversed":
        return "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-100 dark:border-orange-800/60";
      default:
        return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300 border border-neutral-100 dark:border-neutral-800/70";
    }
  };

  const getStatusDotClass = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "rejected":
        return "bg-red-500";
      case "reversed":
        return "bg-orange-500";
      default:
        return "bg-neutral-500";
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "Top-Up":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60";
      case "Top-Down":
        return "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-100 dark:border-red-800/60";
      default:
        return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300 border border-neutral-100 dark:border-neutral-800/70";
    }
  };

  const getTypeDotClass = (type) => {
    switch (type) {
      case "Top-Up":
        return "bg-green-500";
      case "Top-Down":
        return "bg-red-500";
      default:
        return "bg-neutral-500";
    }
  };

  const formatDate = (raw) => {
    if (!raw) return "N/A";
    const dateString = String(raw).trim();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dotDateRE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/;
    const hasExplicitTime = /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString);

    let d;
    const m = dateString.match(dotDateRE);
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m;
      d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0,
        MM ? parseInt(MM, 10) : 0,
        0,
        0
      );
    } else {
      d = new Date(dateString);
    }
    if (isNaN(d.getTime())) return raw;

    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d);

    if (!hasExplicitTime) return datePart;

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    });

    const parts = timeFormatter.formatToParts(d);
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
    const hourNum = parseInt(hour, 10);
    const suffix = hourNum < 12 ? "AM" : "PM";
    const timePart = `${hour}:${minute} ${suffix}`;

    return `${datePart}, ${timePart}`;
  };

  const handleDateRangeChange = (range) => setDateRange(range);
  const handleFilterChange = (name, value) => setFilters((f) => ({ ...f, [name]: value }));

  const fetchProviders = async () => {
    try {
      const res = await providersService.searchAll();
      const payload = res.data;
      if (payload?.statusCode === 200 && Array.isArray(payload.data)) {
        setProviderOptions(
          payload.data
            .filter((x) => x && x._id)
            .map((x) => ({
              id: x._id,
              name: x.name || "Unknown Provider",
              value: x._id,
            }))
        );
      }
    } catch (e) {
      console.error("Rejected to fetch providers:", e);
      toast.error(e.response?.data?.message || "Failed to load providers");
    }
  };

  const fetchFilterOptions = async () => {
    setLoadingOptions(true);

    // Independent try/catch per source: a failure loading admins must not
    // prevent the clients filter from loading (and vice versa).
    if (canSeeAdminFilter) try {
      const adminsRes = await usersService.searchAllUsers();
      if (adminsRes.data?.statusCode === 200) {
        setAdminOptions([
          { id: "all-users", name: "All users", value: "" },
          ...(adminsRes.data?.data || []).map((a) => ({
            id: a._id,
            name: `${a.fullName} - (${a.role?.name})`,
            value: a._id,
          })),
        ]);
      }
    } catch (e) {
      console.error("Rejected to load admin options:", e);
      toast.error(e.response?.data?.message || "Failed to load users filter");
    }

    if (canSeeClientFilter) try {
      const clientsRes = await clientsService.searchClients();
      if (clientsRes.data?.statusCode === 200) {
        setClientOptions(
          (clientsRes.data?.data || []).map((c) => ({
            id: c._id,
            name: c.reference || c.fullName,
            value: c._id,
          }))
        );
      }
    } catch (e) {
      console.error("Rejected to load client options:", e);
      toast.error(e.response?.data?.message || "Failed to load clients filter");
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchTransferHistory = async (overrides = {}) => {
    const page = overrides.page ?? pagination.currentPage;
    const limit = overrides.limit ?? pagination.pageSize;

    const effectiveSearch = overrides.search ?? searchQuery;
    const effectiveFilters = overrides.filters ?? filters;
    const effectiveRange = overrides.dateRange ?? dateRange;

    try {
      setLoading(true);

      const body = {
        search: effectiveSearch,
        type: effectiveFilters.type,
        status: effectiveFilters.status,
        platform: effectiveFilters.platform,
        startDate: effectiveRange.start,
        endDate: effectiveRange.end,
        page,
        limit,
        admin: effectiveFilters.admin,
        client: effectiveFilters.client,
        provider: effectiveFilters.provider,
      };

      const res = await transferHistoryService.searchAll(body);

      // Support both shapes:
      // { data: [...], totals, pagination }
      // { data: { data: [...], totals, pagination } }
      const payload = res.data || {};
      const rows = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload?.data?.data)
          ? payload.data.data
          : [];

      const totalsFromApi = payload.totals || payload?.data?.totals || null;
      const p = payload.pagination || payload?.data?.pagination || null;

      setTransferHistory(rows);

      if (totalsFromApi) {
        setTotals({
          totalTopUp: Number(totalsFromApi.totalTopUp) || 0,
          totalWithdraw: Number(totalsFromApi.totalWithdraw) || 0,
        });
      } else {
        setTotals({ totalTopUp: 0, totalWithdraw: 0 });
      }

      if (p) {
        setPagination({
          currentPage: p.currentPage,
          pageSize: p.pageSize,
          totalPages: p.totalPages,
          totalItems: p.totalItems,
        });
        setTotalItems(p.totalItems || 0);
      } else {
        setTotalItems(rows.length || 0);
      }
    } catch (err) {
      console.error("Rejected to load transfer history:", err);
      setTransferHistory([]);
      setTotalItems(0);
      setTotals({ totalTopUp: 0, totalWithdraw: 0 });
    } finally {
      setLoading(false);
    }
  };

  /* Initial fetch + on pagination change */
  useEffect(() => {
    fetchTransferHistory();
    // Only fetch filter data the role can actually access — avoids 403 toasts
    // for roles that don't see those filters anyway
    // Keep these conditions identical to the render gates below, otherwise a
    // select renders permanently empty (or data is fetched and never shown).
    if (canSeeAdminFilter || canSeeClientFilter) fetchFilterOptions();
    if (canSeeProviderFilter) fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize]);

  const applyFilters = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }));
    fetchTransferHistory({ page: 1 });
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery("");
    setDateRange(DEFAULT_RANGE);
  };

  const resetAndRefresh = () => {
    clearFilters();
    setPagination((p) => ({ ...p, currentPage: 1 }));
    fetchTransferHistory({
      page: 1,
      search: "",
      filters: DEFAULT_FILTERS,
      dateRange: DEFAULT_RANGE,
    });
  };

  const handleSearch = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }));
    fetchTransferHistory({ page: 1 });
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((p) => ({ ...p, currentPage: newPage }));
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number.parseInt(e.target.value);
    setPagination((p) => ({ ...p, pageSize: newSize, currentPage: 1 }));
  };

  const handleViewDetails = (tx) => {
    setSelectedTransaction(tx);
    setShowDetailsModal(true);
    setDropdownOpen(null);
  };

  const handleFormSuccess = () => {
    fetchTransferHistory();
    setShowStatusModal(false);
  };

  const displayCurrency = "USD";

  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">
      {/* Summary Cards */}

      {
        currentUser?.role?.name !== 'Client' &&
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <SummaryCard
            title="Total Top-Up"
            value={totals.totalTopUp}
            currency={displayCurrency}
            icon={<ArrowDownRight size={20} />}
            tone="green"
          />
          <SummaryCard
            title="Total Withdraw"
            value={totals.totalWithdraw}
            currency={displayCurrency}
            icon={<ArrowUpRight size={20} />}
            tone="red"
          />
        </div>
      }
      {/* Top bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder="Search transactions..."
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 cursor-pointer ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
            aria-label="Submit search"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Date range */}
        <DateRangePicker
          onRangeChange={handleDateRangeChange}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        {/* More filters */}
        <button
          className={`flex items-center ring-yellow-500/50 gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${showMoreFilters
            ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
            : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
            }`}
          onClick={() => setShowMoreFilters(!showMoreFilters)}
        >
          <span className="text-sm">{showMoreFilters ? "Hide filters" : "More filters"}</span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-sm justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-sm">Apply Filters</span>
          </button>
        )}
      </div>

      {/* Active chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

          {searchQuery && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-sm">
              <MagnifyingGlass size={12} />
              <span>{searchQuery}</span>
              <button
                onClick={() => {
                  setSearchQuery("");
                  fetchTransferHistory({ page: 1, search: "" });
                }}
                className="ml-1"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {(dateRange.start || dateRange.end) && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-sm">
              <CalendarBlank size={12} />
              <span>
                {dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} -{" "}
                {dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"}
              </span>
              <button
                onClick={() => {
                  setDateRange(DEFAULT_RANGE);
                  fetchTransferHistory({ page: 1, dateRange: DEFAULT_RANGE });
                }}
                className="ml-1"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {filters.type && (
            <Chip
              label={filters.type}
              onClear={() => {
                const next = { ...filters, type: "" };
                setFilters(next);
                fetchTransferHistory({ page: 1, filters: next });
              }}
            />
          )}

          {filters.status && (
            <Chip
              label={filters.status[0].toUpperCase() + filters.status.slice(1)}
              onClear={() => {
                const next = { ...filters, status: "" };
                setFilters(next);
                fetchTransferHistory({ page: 1, filters: next });
              }}
            />
          )}

          {filters.platform && (
            <Chip
              label={getPlatformDisplayName(filters.platform)}
              onClear={() => {
                const next = { ...filters, platform: "" };
                setFilters(next);
                fetchTransferHistory({ page: 1, filters: next });
              }}
            />
          )}

          {filters.admin && (
            <Chip
              icon={<User size={12} />}
              label={adminOptions.find((a) => a.value === filters.admin)?.name || "Admin"}
              onClear={() => {
                const next = { ...filters, admin: "" };
                setFilters(next);
                fetchTransferHistory({ page: 1, filters: next });
              }}
            />
          )}

          {filters.client && (
            <Chip
              icon={<User size={12} />}
              label={clientOptions.find((c) => c.value === filters.client)?.name || "Client"}
              onClear={() => {
                const next = { ...filters, client: "" };
                setFilters(next);
                fetchTransferHistory({ page: 1, filters: next });
              }}
            />
          )}

          {filters.provider && (
            <Chip
              icon={<Buildings size={12} />}
              label={providerOptions.find((p) => p.value === filters.provider)?.name || "Provider"}
              onClear={() => {
                const next = { ...filters, provider: "" };
                setFilters(next);
                fetchTransferHistory({ page: 1, filters: next });
              }}
            />
          )}

          <button onClick={resetAndRefresh} className="text-sm text-neutral-500 flex items-center gap-1 ml-2">
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* Expanded filters */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-900/40">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">More filters</h2>
            <button onClick={clearFilters} className="text-sm text-neutral-500 flex items-center gap-1.5">
              <X size={14} />
              <span>Clear all</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <SearchableSelect
              options={typeOptions}
              value={filters.type}
              onChange={(v) => handleFilterChange("type", v)}
              placeholder="Transaction Type"
              icon={<Globe size={16} />}
            />

            <SearchableSelect
              options={platformOptions}
              value={filters.platform}
              onChange={(v) => handleFilterChange("platform", v)}
              placeholder="Platform"
              icon={<Globe size={16} />}
            />

            <SearchableSelect
              options={statusOptions}
              value={filters.status}
              onChange={(v) => handleFilterChange("status", v)}
              placeholder="Status"
              icon={<Globe size={16} />}
            />

            {canSeeAdminFilter && (
              <>
                <SearchableSelect
                  options={adminOptions}
                  value={filters.admin}
                  onChange={(v) => handleFilterChange("admin", v)}
                  placeholder="Admin"
                  icon={<User size={16} />}
                  isLoading={loadingOptions}
                />
              </>
            )}

            {canSeeClientFilter && (
              <SearchableSelect
                  options={[{ id: "all-clients", name: "All Clients", value: "" }, ...clientOptions]}
                  value={filters.client}
                  onChange={(v) => handleFilterChange("client", v)}
                  placeholder="Client"
                  icon={<User size={16} />}
                  isLoading={loadingOptions}
                />
            )}

            {canSeeProviderFilter && (
              <SearchableSelect
                options={[{ id: "all-providers", name: "All Providers", value: "" }, ...providerOptions]}
                value={filters.provider}
                onChange={(v) => handleFilterChange("provider", v)}
                placeholder="Provider"
                icon={<Buildings size={16} />}
              />
            )}

            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} />
                <span className="text-sm">Apply Filters</span>
              </button>
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-900/40 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} />
                <span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-neutral-150 dark:border-neutral-900 rounded-lg bg-white dark:bg-neutral-950">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
          </div>
        ) : transferHistory.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-lg">
              <table className="min-w-full divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50/80 backdrop-blur-sm dark:bg-neutral-900/40">
                  <tr>
                    <Th>Date</Th>
                    <Th>Platform</Th>
                    <Th>Ad Account</Th>
                    <Th>Net Amount</Th>

                    <Th>{currentUser?.role.name !== "Client" ? "Client Fee" : "Fees"}</Th>
                    {currentUser?.role.name !== "Client" && (
                      <>
                        <Th>Provider Fee</Th>
                        <Th>Weberz Fee</Th>
                      </>
                    )}
                    <Th>Total</Th>

                    <Th>Type</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>

                <tbody className="bg-white text-start dark:bg-neutral-950 divide-y divide-neutral-100 dark:divide-neutral-800">
                  {transferHistory.map((t) => {
                    const currency = t.currency || "USD";

                    // ✅ platform: try both stored fields, then fallback to populated accounts
                    const platform =
                      t.destination_platform ||
                      t.source_platform ||
                      t.destination_account?.platform ||
                      t.source_account?.platform;

                    // ✅ Withdraw (Top-Down): NO provider/profit, and Net Amount shown as TOTAL (withdraw + fee)
                    const isTopDown = t.type === "Top-Down";
                    const netDisplay = isTopDown
                      ? Number(t.net_amount_received || 0)
                      : Number(t.net_amount_received || 0);

                    const showProviderProfit = currentUser?.role?.name !== "Client" && !isTopDown;

                    return (
                      <tr
                        key={t._id}
                        className="hover:bg-neutral-50/80 dark:hover:bg-neutral-900/60 transition-colors"
                      >
                        <Td>
                          <div className="flex flex-col text-sm">
                            <span className="font-[400] text-neutral-800 dark:text-neutral-100">
                              {formatDate(t.createdAt)}
                            </span>
                          </div>
                        </Td>

                        <Td>
                          <div className="flex items-center gap-2">
                            {getPlatformIcon(platform)}
                            <span className="text-sm font-[300]">
                              {getPlatformDisplayName(platform)}
                            </span>
                          </div>
                        </Td>

                        <Td>
                          <span className="text-[14px] font-[300]">
                            {t.destination_account?.account_name || "-"}
                          </span>
                        </Td>


                        <Td className="font-[400] text-sm">
                          {formatCurrency(netDisplay, currency)}
                        </Td>

                        <Td>
                          {t.client_fee_amount || t.client_fee_percentage ? (
                            <div className="text-sm flex justify-center items-center gap-2 flex-col">
                              <div className="font-[400]">
                                {formatCurrency(Number(t.client_fee_amount || 0), currency)}
                              </div>
                              <div className="text-neutral-500">
                                {Number(t.client_fee_percentage || 0)}%
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-center items-center ">
                              <span className="text-neutral-400   mx-autotext-right text-sm">-</span>

                            </div>
                          )}
                        </Td>

                        {currentUser?.role.name !== "Client" && (
                          <>
                            <Td>
                              {showProviderProfit ? (
                                t.provider_fee_amount || t.provider_fee_percentage ? (
                                  <div className="text-sm flex justify-center items-center gap-2 flex-col">
                                    <div className="font-[400]">
                                      {formatCurrency(Number(t.provider_fee_amount || 0), currency)}
                                    </div>
                                    <div className="text-neutral-500">
                                      {Number(t.provider_fee_percentage || 0)}%
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex justify-center items-center ">
                                    <span className="text-neutral-400   mx-autotext-right text-sm">-</span>

                                  </div>)
                              ) : (
                                <div className="flex justify-center items-center ">
                                  <span className="text-neutral-400   mx-autotext-right text-sm">-</span>

                                </div>)}
                            </Td>

                            <Td className={showProviderProfit && Number(t.profit_amount || 0) >= 0 ? "text-green-600" : showProviderProfit ? "text-red-600" : ""}>
                              {showProviderProfit ? (
                                <div className="text-sm flex justify-center items-center gap-2 flex-col">
                                  <div className="font-[400]">
                                    {formatCurrency(Number(t.profit_amount || 0), currency)}
                                  </div>
                                  <div className="text-neutral-500">
                                    {Number((t.client_fee_percentage || 0) - (t.provider_fee_percentage || 0))}%
                                  </div>
                                </div>
                              ) : (
                                <div className="flex justify-center items-center ">
                                  <span className="text-neutral-400   mx-autotext-right text-sm">-</span>

                                </div>)}
                            </Td>
                          </>
                        )}

                        <Td className="font-[400] text-sm">
                          {formatCurrency(Number(t.amount || 0), currency)}
                        </Td>
                        <Td>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-[400] ${getTypeBadgeColor(
                              t.type
                            )}`}
                          >
                            <span
                              className={`w-2 h-2 mr-1.5 rounded-full ${getTypeDotClass(
                                t.type
                              )}`}
                            ></span>
                            {t.type === "Top-Up" ? "Top-Up" : t.type === "Top-Down" ? "Withdraw" : t.type}
                          </span>
                        </Td>

                        <Td>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-[400] ${getStatusBadgeColor(
                              t.status
                            )}`}
                          >
                            <span
                              className={`w-2 h-2 mr-1.5 rounded-full ${getStatusDotClass(
                                t.status
                              )}`}
                            ></span>
                            {currentUser?.role?.name === "Client"
                              ? getClientStatusDisplay(t.status)
                              : t.status?.[0]?.toUpperCase() + t.status?.slice(1)}
                          </span>
                        </Td>

                        <Td className="text-sm font-[300] items-center mt-1.5 flex gap-2">
                          {canRead && (
                            <button
                              onClick={() => handleViewDetails(t)}
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-600/10 p-1.5 rounded-xl transition-colors"
                            >
                              <Eye size={14} />
                            </button>
                          )}

                          {currentUser?.role?.name !== "Client" && canUpdate && (
                            <button
                              onClick={() => {
                                setSelectedTransaction(t);
                                setShowStatusModal(true);
                                setDropdownOpen(null);
                              }}
                              className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 bg-amber-600/10 p-1.5 rounded-xl transition-colors"
                            >
                              <PencilSimple size={14} />
                            </button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 bg-neutral-50/60 dark:bg-neutral-900/40 rounded-b-lg">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-[13px] text-neutral-600 dark:text-neutral-400">
                  Showing {transferHistory.length} of {pagination.totalItems} transfers
                </span>

                <div className="ml-4 flex items-center gap-2">
                  <span className="text-[12px] text-neutral-500 dark:text-neutral-400">Rows</span>

                  <div className="relative">
                    <select
                      value={pagination.pageSize}
                      onChange={handlePageSizeChange}
                      className="h-8 pl-3 pr-8 text-[12px] border border-neutral-200/80 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-neutral-100 shadow-sm focus:outline-none focus:ring-1 focus:ring-neutral-300 dark:focus:ring-neutral-600 appearance-none cursor-pointer"
                    >
                      <option value={5}>5 / page</option>
                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </select>
                    <CaretDown
                      size={14}
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn disabled={pagination.currentPage === 1} onClick={() => handlePageChange(1)} aria="First page">
                  <CaretDoubleLeft size={14} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  aria="Previous page"
                >
                  <CaretLeft size={14} />
                </PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map((page, idx) =>
                    page === "..." ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-1 text-neutral-500 dark:text-neutral-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-7 h-7 flex items-center justify-center rounded-xl text-[13px] ${pagination.currentPage === page
                          ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                          : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                          }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  aria="Next page"
                >
                  <CaretRight size={14} />
                </PageBtn>

                <PageBtn
                  disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                  onClick={() => handlePageChange(pagination.totalPages)}
                  aria="Last page"
                >
                  <CaretDoubleRight size={14} />
                </PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>
            <h3 className="text-xl font-[400] text-neutral-900 dark:text-white mb-2">No Transactions Found</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {searchQuery ||
                Object.values(filters).some(Boolean) ||
                dateRange.start ||
                dateRange.end
                ? "No transactions match your search criteria. Try adjusting your filters or search term."
                : "There are no transactions in the system yet."}
            </p>
            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-900/40 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <TransactionDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        transaction={selectedTransaction}
        currentUser={currentUser}
      />

      {currentUser?.role?.name !== "Client" && canUpdate && (
        <ChangeStatusModal
          isOpen={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          transaction={selectedTransaction}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}

/* ────────────────────────────── Small UI helpers ────────────────────────────── */
function Th({ children }) {
  return (
    <th className="px-6 py-5 text-left text-[12px] font-[400] text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.08em] whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-200 ${className}`}>
      {children}
    </td>
  );
}

function Chip({ label, onClear, icon }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-sm">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1">
        <X size={12} />
      </button>
    </div>
  );
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1 rounded-xl ${disabled
        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        }`}
      aria-label={aria}
    >
      {children}
    </button>
  );
}

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages];
  if (currentPage >= totalPages - 2) return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

/* Summary Card */
function SummaryCard({ title, value, currency, icon, tone = "neutral" }) {
  const toneMap = {
    green: "bg-white dark:bg-neutral-950 text-green-600 dark:text-green-500 border-neutral-150 dark:border-neutral-900",
    red: "bg-white dark:bg-neutral-950 text-red-600 dark:text-red-500 border-neutral-150 dark:border-neutral-900",
  }

  const pillMap = {
    green: "bg-green-600/10 text-green-700 dark:bg-green-500/10 dark:text-green-600",
    red: "bg-red-600/10 text-red-700 dark:bg-red-500/10 dark:text-red-600",
  }
  return (
    <div className={`border flex items-center justify-between rounded-xl p-5 ${toneMap[tone]}`}>
      <div className="flex flex-col justify-center items-start">
        <div className="text-xs uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">{title}</div>
        <div className="mt-1 text-2xl font-[400]">
          <span>
            {Number(value || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>{" "}
          <span className="text-sm font-[400] ">{currency}</span>
        </div>
      </div>

      <div className={`p-3 rounded-xl ${pillMap[tone]}`}>{icon}</div>
    </div>
  );
}
